import { FC } from 'react';
import { withStyles, Card, CircleButton, Icon, InteracWordmark, MaterialSymbol, PaymentMethodType } from 'vopay';

import styles from './Options.module.scss';
import { useTranslation } from 'react-i18next';

import { Title } from '#shared/components/UI';

const cn = withStyles(styles);

export type OptionPaymentMethod = Extract<
  PaymentMethodType,
  'BankAccount' | 'CreditCard' | 'DebitCard' | 'Email' | 'PayPal' | 'Venmo' | 'Zelle'
>;

type LockedSelectOptions = { [featureKey: string]: string };

type OptionsProps = {
  omit?: OptionPaymentMethod[];
  onClose: () => void;
  onSelect: (option: OptionPaymentMethod) => void;
  // Host facts the portal read from its ClientData/LockedFeatures providers.
  country: string;
  lockedSelectOptions?: LockedSelectOptions;
  // Locked-feature upsell lives in the host; absent means a locked click does nothing.
  onLockedFeature?: (featureName: string) => void;
};

const Options: FC<OptionsProps> = ({ onClose, omit = [], onSelect, country, lockedSelectOptions, onLockedFeature }) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.Customers.PaymentMethodsTab.ProvidePaymentMethod.Options',
  });

  const isUS = country === 'US';

  return (
    <Card hoverEffect={false} className={cn('optionsContainer')}>
      <CircleButton
        icon="close"
        style={{ display: 'block', marginLeft: 'auto' }}
        onClick={onClose}
        circleFill={false}
        size="large"
      />

      <Title level="4">{t('title', { defaultValue: 'Select Payment Method' })}</Title>

      <div className={cn('cards')}>
        {!omit.includes('BankAccount') && (
          <CardLink
            lockedFeature="Bank Account"
            lockedSelectOptions={lockedSelectOptions}
            onLockedFeature={onLockedFeature}
            onClick={() => onSelect('BankAccount')}
          >
            <Icon icon="bank2" size="lg" color="primary-dark-transparent" />

            <span className={cn('label')}>
              {lockedSelectOptions && lockedSelectOptions['Bank Account'] && (
                <MaterialSymbol icon="lock" fillAmount={1} />
              )}

              {t('bankAccount', { defaultValue: 'Bank Account' })}
            </span>
          </CardLink>
        )}

        {!omit.includes('CreditCard') && (
          <CardLink
            lockedFeature="Credit Card"
            lockedSelectOptions={lockedSelectOptions}
            onLockedFeature={onLockedFeature}
            onClick={() => onSelect('CreditCard')}
          >
            <Icon icon="creditCard2" size="lg" color="primary-dark-transparent" />

            <span className={cn('label')}>
              {lockedSelectOptions && lockedSelectOptions['Credit Card'] && (
                <MaterialSymbol icon="lock" fillAmount={1} />
              )}

              {t('creditCard', { defaultValue: 'Credit Card' })}
            </span>
          </CardLink>
        )}

        {!omit.includes('DebitCard') && (
          <CardLink
            lockedFeature="Debit Card"
            lockedSelectOptions={lockedSelectOptions}
            onLockedFeature={onLockedFeature}
            onClick={() => onSelect('DebitCard')}
          >
            <Icon icon="creditCard2" size="lg" color="primary-dark-transparent" />

            <span className={cn('label')}>
              {lockedSelectOptions && lockedSelectOptions['Debit Card'] && (
                <MaterialSymbol icon="lock" fillAmount={1} />
              )}{' '}
              {t('debitCard', { defaultValue: 'Debit Card' })}
            </span>
          </CardLink>
        )}

        {/* Interac e-Transfer is Canada-only. */}
        {!omit.includes('Email') && !isUS && (
          <CardLink
            lockedFeature="Email"
            lockedSelectOptions={lockedSelectOptions}
            onLockedFeature={onLockedFeature}
            onClick={() => onSelect('Email')}
          >
            <Icon icon="interacColor" size="lg" color="none" />

            <span className={cn('label')}>
              {lockedSelectOptions && lockedSelectOptions['Email'] && <MaterialSymbol icon="lock" fillAmount={1} />}
              <InteracWordmark />
            </span>
          </CardLink>
        )}

        {!omit.includes('PayPal') && (
          <CardLink
            lockedFeature="PayPal"
            lockedSelectOptions={lockedSelectOptions}
            onLockedFeature={onLockedFeature}
            onClick={() => onSelect('PayPal')}
          >
            <Icon icon="paypal" size="lg" color="none" />

            <span className={cn('label')}>
              {lockedSelectOptions && lockedSelectOptions['PayPal'] && <MaterialSymbol icon="lock" fillAmount={1} />}
              {t('paypal', { defaultValue: 'PayPal' })}
            </span>
          </CardLink>
        )}

        {isUS && !omit.includes('Venmo') && (
          <CardLink
            lockedFeature="Venmo"
            lockedSelectOptions={lockedSelectOptions}
            onLockedFeature={onLockedFeature}
            onClick={() => onSelect('Venmo')}
          >
            <Icon icon="venmo" size="lg" color="none" />

            <span className={cn('label')}>
              {lockedSelectOptions && lockedSelectOptions['Venmo'] && <MaterialSymbol icon="lock" fillAmount={1} />}
              {t('venmo', { defaultValue: 'Venmo' })}
            </span>
          </CardLink>
        )}

        {isUS && !omit.includes('Zelle') && (
          <CardLink
            lockedFeature="Zelle"
            lockedSelectOptions={lockedSelectOptions}
            onLockedFeature={onLockedFeature}
            onClick={() => onSelect('Zelle')}
          >
            <Icon icon="zelle" size="lg" color="none" />

            <span className={cn('label')}>
              {lockedSelectOptions && lockedSelectOptions['Zelle'] && <MaterialSymbol icon="lock" fillAmount={1} />}
              {t('zelle', { defaultValue: 'Zelle' })}
            </span>
          </CardLink>
        )}
      </div>
    </Card>
  );
};

export default Options;

function CardLink({
  children,
  onClick,
  lockedFeature,
  lockedSelectOptions,
  onLockedFeature,
}: {
  children: React.ReactNode;
  onClick: () => void;
  lockedFeature: string;
  lockedSelectOptions?: LockedSelectOptions;
  onLockedFeature?: (featureName: string) => void;
}) {
  const isLocked = lockedSelectOptions && lockedSelectOptions[lockedFeature];

  return (
    <button
      onClick={() => {
        if (isLocked) {
          onLockedFeature?.(lockedSelectOptions[lockedFeature]);
        } else {
          onClick();
        }
      }}
    >
      <Card
        style={{
          borderWidth: 1,
          borderColor: '#2d2d2d07',
        }}
        className={cn('card')}
      >
        <div className={cn('cardContent', isLocked && 'locked')}>{children}</div>
      </Card>
    </button>
  );
}
